import { Modal, Form, Select, Checkbox, Button, Table, Spin, Alert, Typography } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import React from "react";
import { PlatformCaptionLanguagesData } from "../../utils";

interface DistributionPlatform {
    id: string;
    key: string;
    name: string;
    created_at: string;
}

interface BulkSegmentationModalProps {
	visible: boolean;
	onClose: () => void;
	onOk: () => void;
	form: any;
	distributionPlatformData: DistributionPlatform[];
	captionOrder: string[];
	captionInfo: PlatformCaptionLanguagesData | null;
	isCaptionInfoLoading: boolean;
	onPlatformChange: (platformId?: string) => void;
	onMoveCaptionLanguage: (index: number, direction: -1 | 1) => void;
}

const capitalize = (lang: string) => (lang ? lang.charAt(0).toUpperCase() + lang.slice(1) : lang);

const BulkSegmentationModal: React.FC<BulkSegmentationModalProps> = ({
	visible,
	onClose,
	onOk,
	form,
	distributionPlatformData,
	captionOrder,
	captionInfo,
	isCaptionInfoLoading,
	onPlatformChange,
	onMoveCaptionLanguage,
}) => {
	// CEA-608 carries two languages: per video the first two ranked languages it
	// actually has become CC1 and CC3.
	const ccAssignment = (comboLanguages: string[]) => {
		const ranked = captionOrder.filter((lang) => comboLanguages.includes(lang));
		return { cc1: ranked[0], cc3: ranked[1] };
	};

	const comboRows = captionInfo
		? [
			...captionInfo.combos.map((combo, i) => {
				const { cc1, cc3 } = ccAssignment(combo.languages);
				return {
					key: `combo-${i}`,
					languages: combo.languages.map(capitalize).join(" + "),
					videos: combo.videos,
					cc1: cc1 ? capitalize(cc1) : "—",
					cc3: cc3 ? capitalize(cc3) : "—",
				};
			}),
			...(captionInfo.videosWithoutCaptions > 0
				? [{
					key: "no-captions",
					languages: "(no SCC captions)",
					videos: captionInfo.videosWithoutCaptions,
					cc1: "—",
					cc3: "—",
				}]
				: []),
		]
		: [];

	return (
		<Modal
			title="Bulk Video Segmentation"
			open={visible}
			width={680}
			onCancel={onClose}
			footer={
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
					<Button
						type="default"
						onClick={onClose}
					>
						Cancel
					</Button>
					<Button
						type="primary"
						onClick={onOk}
					>
						Segment Videos
					</Button>
				</div>
			}
		>
			<Form form={form} layout="vertical">
				<Form.Item
					label="Distribution Platform"
					name="distributionPlatform"
					style={{ marginBottom: 8 }}
				>
					<Select
						options={distributionPlatformData.map(item => ({
							value: item.id,
							label: item.name,
						}))}
						placeholder="Select a platform"
						allowClear
						onChange={(value) => onPlatformChange(value)}
					/>
				</Form.Item>

				{isCaptionInfoLoading && (
					<div style={{ textAlign: "center", margin: "16px 0" }}>
						<Spin size="small" /> <Typography.Text type="secondary">Loading caption languages…</Typography.Text>
					</div>
				)}

				{!isCaptionInfoLoading && captionInfo && captionOrder.length === 0 && (
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 8 }}
						message={`No SCC captions found for any of this platform's ${captionInfo.totalVideos} videos — segments will be cut without embedded captions.`}
					/>
				)}

				{!isCaptionInfoLoading && captionOrder.length > 0 && (
					<>
						<Typography.Text strong>Caption language priority (CEA-608)</Typography.Text>
						<div style={{ margin: "8px 0", border: "1px solid #f0f0f0", borderRadius: 6 }}>
							{captionOrder.map((lang, index) => (
								<div
									key={lang}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "4px 12px",
										borderTop: index === 0 ? "none" : "1px solid #f0f0f0",
									}}
								>
									<span>
										<Typography.Text type="secondary" style={{ marginRight: 12 }}>{index + 1}</Typography.Text>
										{capitalize(lang)}
									</span>
									<span>
										<Button
											size="small"
											type="text"
											icon={<ArrowUpOutlined />}
											disabled={index === 0}
											onClick={() => onMoveCaptionLanguage(index, -1)}
										/>
										<Button
											size="small"
											type="text"
											icon={<ArrowDownOutlined />}
											disabled={index === captionOrder.length - 1}
											onClick={() => onMoveCaptionLanguage(index, 1)}
										/>
									</span>
								</div>
							))}
						</div>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							Per video, the first two available languages in this order are embedded as CC1 / CC3.
							The order is saved for the platform when you segment.
						</Typography.Text>
						<Table
							size="small"
							style={{ margin: "12px 0 8px" }}
							pagination={false}
							dataSource={comboRows}
							columns={[
								{ title: "Videos by SCC languages", dataIndex: "languages", key: "languages" },
								{ title: "Videos", dataIndex: "videos", key: "videos", width: 70, align: "right" as const },
								{ title: "CC1", dataIndex: "cc1", key: "cc1", width: 90 },
								{ title: "CC3", dataIndex: "cc3", key: "cc3", width: 90 },
							]}
							summary={() => (
								<Table.Summary.Row>
									<Table.Summary.Cell index={0}>
										<Typography.Text strong>Total</Typography.Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={1} align="right">
										<Typography.Text strong>{captionInfo?.totalVideos ?? 0}</Typography.Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={2} />
									<Table.Summary.Cell index={3} />
								</Table.Summary.Row>
							)}
						/>
					</>
				)}

				<Form.Item name="redoExistingSegments" valuePropName="checked" style={{ marginBottom: 0 }}>
					<Checkbox>Redo existing segments</Checkbox>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default BulkSegmentationModal;
